import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { formatBytes, formatTime, getKindLabel } from "../../utils/messageHelpers";
import "./MessageBubble.css";

/** Fix URLs the LLM sometimes mangles (e.g. "httpshttps://..."). */
function sanitizeUrl(url) {
  if (!url) return url;
  return url.replace(/^https?(?=https?:\/\/)/, "");
}

// Custom markdown components shared by both Chat surfaces — links open in
// a new tab, images become clickable lightboxes.
const markdownComponents = {
  a: ({ node, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer" />
  ),
  img: ({ node, ...props }) => {
    const src = sanitizeUrl(props.src);
    return (
      <a href={src} target="_blank" rel="noopener noreferrer">
        <img
          {...props}
          src={src}
          style={{
            maxWidth: "400px",
            maxHeight: "400px",
            borderRadius: "8px",
            cursor: "pointer",
            display: "block",
            marginTop: "8px",
          }}
          loading="lazy"
        />
      </a>
    );
  },
};

function KindBadge({ kind }) {
  const meta = getKindLabel(kind);
  if (!meta) return null;
  return (
    <span className={`message-kind-badge kind-${kind}`} data-icon={meta.icon}>
      {meta.label}
    </span>
  );
}

function Attachments({ attachments }) {
  if (!attachments?.length) return null;
  return (
    <div className="message-attachments">
      {attachments.map((att, j) => (
        <div key={j} className="message-attachment">
          {att.contentType?.startsWith("image/") ? (
            <div className="attachment-thumb">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <span>{att.filename}</span>
            </div>
          ) : (
            <div className="attachment-file-badge">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <span>{att.filename}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Artifacts({ artifacts, variant }) {
  if (!artifacts?.length) return null;
  const wrapperCls = variant === "widget" ? "anna-widget-artifacts" : "message-artifacts";
  const itemCls = variant === "widget" ? "anna-widget-artifact" : "message-artifact";
  const iconCls = variant === "widget" ? "anna-widget-artifact-icon" : "artifact-icon";
  return (
    <div className={wrapperCls}>
      {artifacts.map((art, j) => (
        <a
          key={j}
          className={itemCls}
          href={art.download_url}
          target="_blank"
          rel="noopener noreferrer"
          title={`Open ${art.filename || "HTML file"}`}
        >
          <svg
            className={iconCls}
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="12" y1="18" x2="12" y2="12" />
            <polyline points="9 15 12 18 15 15" />
          </svg>
          <div className={variant === "widget" ? "anna-widget-artifact-meta" : "artifact-meta"}>
            <div className={variant === "widget" ? "anna-widget-artifact-title" : "artifact-title"}>
              {art.title || art.filename || "HTML file"}
            </div>
            <div className={variant === "widget" ? "anna-widget-artifact-sub" : "artifact-sub"}>
              {(art.filename || "document.html") + (art.size ? ` · ${formatBytes(art.size)}` : "")}
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}

/**
 * Shared chat message renderer used by both the main Chat view and the
 * floating ChatWidget. Branches on `m.kind` (see `doc/ChatIngestion.md`)
 * to show a small badge for non-conversation messages while keeping the
 * markdown/attachment/artifact rendering identical across surfaces.
 *
 * `variant`:
 *   - "full"   → main Chat.jsx classes (.message, .message-role, …)
 *   - "widget" → ChatWidget.jsx classes (.anna-widget-msg, …)
 */
// Source labels for non-web assistant messages (emails, routines, speaker, etc.)
const SOURCE_LABELS = {
  gmail:        { label: "Email", icon: "📧" },
  routine:      { label: "Routine", icon: "🔄" },
  notification: { label: "Reminder", icon: "⏰" },
  speaker:      { label: "Speaker", icon: "🔊" },
  telegram:     { label: "Telegram", icon: "💬" },
  device:       { label: "Device", icon: "🎤" },
  singularity:  { label: "Monitor", icon: "📡" },
  lupulus:      { label: "Brewing", icon: "🍺" },
};

function SourceLabel({ source, role }) {
  // Show on assistant messages from non-web sources, and on user messages from speaker/device
  if (!source || source === "web" || source === "chat") return null;
  if (role === "user" && source !== "speaker" && source !== "device" && source !== "telegram") return null;
  const meta = SOURCE_LABELS[source];
  if (!meta) return null;
  return (
    <span className={`message-source-label source-${source}`}>
      {meta.icon} {meta.label}
    </span>
  );
}

/**
 * Collapse consecutive `actionNote` messages into a single group entry so a
 * burst of actions renders as ONE discrete line. Both chat surfaces run their
 * (already filtered) message list through this before mapping to bubbles.
 */
export function groupActionNotes(messages) {
  const out = [];
  for (const m of messages) {
    if (m?.actionNote) {
      const last = out[out.length - 1];
      if (last?.actionGroup) {
        last.notes.push(m);
      } else {
        out.push({ actionGroup: true, notes: [m] });
      }
    } else {
      out.push(m);
    }
  }
  return out;
}

/**
 * Collapsed inline action marker. Anna can fire many actions in one turn, so
 * the whole consecutive burst renders as ONE discrete horizontal line across
 * the chat with a small caret — click to expand the list of actions. Shared
 * by both surfaces.
 */
function ActionNoteGroup({ notes }) {
  const [open, setOpen] = useState(false);
  const failures = notes.filter((n) => n.success === false).length;
  const allOk = failures === 0;
  const images = notes.flatMap((n) => Array.isArray(n.images) ? n.images : [])
    .filter((img) => img?.preview_url || img?.url);
  const label =
    notes.length === 1
      ? notes[0].action || "action"
      : `${notes.length} actions`;
  return (
    <div className={`action-note${open ? " open" : ""}`}>
      <button
        type="button"
        className="action-note-line"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={open ? "Hide actions" : "Show actions"}
      >
        <span className="action-note-caret">▸</span>
        <span className="action-note-label">
          {label}
          <span className={`action-note-status ${allOk ? "ok" : "fail"}`}>
            {allOk ? "✓" : `✕ ${failures}`}
          </span>
        </span>
      </button>
      {open && (
        <div className="action-note-list">
          {notes.map((n, i) => {
            const ok = n.success !== false;
            return (
              <div key={i} className="action-note-item">
                <span className={`action-note-status ${ok ? "ok" : "fail"}`}>{ok ? "✓" : "✕"}</span>
                <span className="action-note-item-name">{n.action || "action"}</span>
                {n.content && <span className="action-note-item-detail">{n.content}</span>}
              </div>
            );
          })}
        </div>
      )}
      {images.length > 0 && (
        <div className="action-note-images">
          {images.map((img, i) => {
            const src = sanitizeUrl(img.preview_url || img.url);
            return (
              <a key={`${src}-${i}`} href={src} target="_blank" rel="noopener noreferrer">
                <img src={src} alt={`Generated image ${i + 1}`} loading="lazy" />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function MessageBubble({ m, variant = "full", renderAvatar }) {
  const role = m.role || "assistant";
  const kind = m.kind || "conversation";
  const source = m.source || "";
  const assistantName = "Anna";

  // Inline action markers collapse to a single thin line on both surfaces.
  if (m.actionGroup) {
    return <ActionNoteGroup notes={m.notes} />;
  }
  if (m.actionNote) {
    return <ActionNoteGroup notes={[m]} />;
  }
  if (m.detachedNote) {
    return <div className="detached-note">{m.content}</div>;
  }

  // Add source class for non-web messages (colored border / tint)
  const sourceClass = (source && source !== "web" && source !== "chat")
    ? ` source-${source}` : "";

  if (variant === "widget") {
    return (
      <div className={`anna-widget-msg ${role}`}>
        <div className="anna-widget-avatar">{renderAvatar?.(role)}</div>
        <div className="anna-widget-bubble">
          {role === "assistant" ? (
            <div className="anna-widget-md">
              <KindBadge kind={kind} />
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {m.content || ""}
              </ReactMarkdown>
              {m.streaming && (
                <span className="anna-widget-dots"><span /><span /><span /></span>
              )}
              <Artifacts artifacts={m.artifacts} variant="widget" />
            </div>
          ) : (
            <div className="anna-widget-text">
              <KindBadge kind={kind} />
              {m.content}
            </div>
          )}
        </div>
      </div>
    );
  }

  // "full" — main Chat.jsx
  return (
    <div className={`message ${role}${sourceClass}`}>
      {role !== "system" && (
        <div className="message-role">
          {role === "user" ? "You" : assistantName}
          <SourceLabel source={source} role={role} />
        </div>
      )}
      <div className="message-body">
        <div className="message-content">
          <KindBadge kind={kind} />
          {role === "assistant" ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {m.content || ""}
            </ReactMarkdown>
          ) : (
            m.content
          )}
          {m.streaming && !m.checking && <span className="cursor-blink">▊</span>}
          <Attachments attachments={m.attachments} />
          <Artifacts artifacts={m.artifacts} variant="full" />
        </div>
        {m.checking && (
          <div className="checking-indicator">
            <span className="checking-spinner"></span>
            <span className="checking-text">{m.checking}</span>
          </div>
        )}
      </div>
      <span className="message-time">{formatTime(m.timestamp)}</span>
    </div>
  );
}
